import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Save, Eye, EyeOff, Loader2, Mail } from "lucide-react";

/**
 * EmailTemplatesSection — v3.8.4.
 *
 * Admins edit two email templates (customer confirmation + admin notification)
 * with placeholder-tag support: `{customer_name}`, `{booking_reference}`,
 * `{vehicle_title}`, `{pickup_location}`, `{pickup_date}`, `{return_location}`,
 * `{return_date}`, `{total_amount}`, `{deposit_amount}`, `{balance_due}`,
 * `{payment_method}`, `{bank_details}`. A preview toggle live-renders the
 * template with a synthetic sample payload so admins can see exactly what
 * customers will receive.
 */
const TEMPLATE_META = {
  customer_booking_confirmation: {
    label:  "Customer · Booking confirmation",
    hint:   "Sent to the customer after a booking is created.",
  },
  admin_new_booking_notification: {
    label:  "Admin · New booking notification",
    hint:   "Sent to the ops mailbox whenever a new booking is created.",
  },
};

const PLACEHOLDERS = [
  "customer_name", "booking_reference", "vehicle_title",
  "pickup_location", "pickup_date", "return_location", "return_date",
  "total_amount", "deposit_amount", "balance_due", "payment_method", "bank_details",
];

export default function EmailTemplatesSection() {
  const [tpls, setTpls] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/email-templates")
      .then((r) => setTpls(r.data || []))
      .catch(() => setTpls([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <p className="text-sm text-neutral-500">Loading templates…</p>;
  if (tpls.length === 0) return <p className="text-sm text-neutral-500">No templates.</p>;

  return (
    <div className="space-y-6" data-testid="email-templates-section">
      {tpls.map((t) => (
        <TemplateEditor
          key={t.key}
          template={t}
          onSaved={(next) => setTpls((prev) => prev.map((p) => p.key === next.key ? next : p))}
        />
      ))}
    </div>
  );
}

function TemplateEditor({ template, onSaved }) {
  const [subject, setSubject] = useState(template.subject);
  const [body,    setBody]    = useState(template.body);
  const [isActive, setIsActive] = useState(template.is_active !== false);
  const [saving, setSaving] = useState(false);
  const [previewOn, setPreviewOn] = useState(false);
  const [preview, setPreview] = useState({ subject: "", body: "" });
  const [previewing, setPreviewing] = useState(false);

  const meta = TEMPLATE_META[template.key] || { label: template.key, hint: "" };

  useEffect(() => {
    if (!previewOn) return;
    setPreviewing(true);
    api.post(`/admin/email-templates/${template.key}/preview`)
      .then((r) => setPreview(r.data))
      .catch((err) => toast.error(formatApiError(err?.response?.data?.detail) || err.message))
      .finally(() => setPreviewing(false));
  }, [previewOn, subject, body, template.key]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/email-templates/${template.key}`, {
        subject, body, is_active: isActive,
      });
      toast.success("Template saved");
      onSaved(data);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const insertTag = (tag) => setBody((b) => `${b}{${tag}}`);

  return (
    <div className="border border-black/10 dark:border-white/10 rounded-lg p-4 space-y-3" data-testid={`tpl-${template.key}`}>
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <p className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 flex items-center gap-1.5"><Mail size={12} /> Template</p>
          <h4 className="font-medium text-sm mt-1">{meta.label}</h4>
          <p className="text-[11px] text-neutral-500 mt-0.5">{meta.hint}</p>
        </div>
        <label className="text-xs inline-flex items-center gap-1.5">
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            data-testid={`tpl-${template.key}-active`}
          />
          Active
        </label>
      </header>

      <div>
        <label className="block text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-1.5">Subject</label>
        <input
          type="text"
          className="w-full px-3 py-2 border border-black/15 dark:border-white/15 rounded text-sm bg-white dark:bg-[#111827]"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          data-testid={`tpl-${template.key}-subject`}
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Body (HTML)</label>
          <button
            type="button"
            onClick={() => setPreviewOn((v) => !v)}
            className="text-xs px-2.5 py-1 rounded border border-black/10 dark:border-white/15 hover:border-black dark:hover:border-white inline-flex items-center gap-1"
            data-testid={`tpl-${template.key}-preview-toggle`}
          >
            {previewOn ? <EyeOff size={11} /> : <Eye size={11} />}
            {previewOn ? "Hide preview" : "Preview email"}
          </button>
        </div>
        <textarea
          rows={10}
          className="w-full px-3 py-2 border border-black/15 dark:border-white/15 rounded text-xs font-mono bg-white dark:bg-[#111827]"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          data-testid={`tpl-${template.key}-body`}
        />
        <div className="mt-2 flex flex-wrap gap-1.5">
          <span className="text-[10px] uppercase tracking-[0.15em] text-neutral-500 mr-1">Insert:</span>
          {PLACEHOLDERS.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => insertTag(p)}
              className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-200 dark:hover:bg-neutral-700"
              data-testid={`tpl-${template.key}-tag-${p}`}
            >
              {`{${p}}`}
            </button>
          ))}
        </div>
      </div>

      {previewOn && (
        <div className="border border-emerald-300/60 dark:border-emerald-700/50 bg-emerald-50/50 dark:bg-emerald-950/20 rounded p-3" data-testid={`tpl-${template.key}-preview`}>
          <p className="text-[10px] uppercase tracking-[0.2em] text-emerald-800 dark:text-emerald-300 mb-2">Live preview</p>
          {previewing ? (
            <p className="text-xs text-neutral-500"><Loader2 size={12} className="inline animate-spin" /> Rendering…</p>
          ) : (
            <>
              <p className="text-xs font-semibold">Subject: {preview.subject}</p>
              <div className="mt-2 border-t border-black/10 pt-2 text-xs bg-white dark:bg-[#111827] p-3 rounded" dangerouslySetInnerHTML={{ __html: preview.body }} />
            </>
          )}
        </div>
      )}

      <div className="flex justify-end">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="bg-black text-white dark:bg-white dark:text-black text-sm px-4 py-2 rounded inline-flex items-center gap-2 disabled:opacity-50"
          data-testid={`tpl-${template.key}-save`}
        >
          {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
          Save template
        </button>
      </div>
    </div>
  );
}
